import React, { useEffect, useMemo, useRef, useState } from "react";
import { formatTime, hasLocation, isStale, searchText } from "./trackingRules";

const PAGE = 20;

/** One vehicle card: activity, trip status, vehicle no + GPS dot, driver, GPS time, crew, ride id. */
function VehicleCard({ v, selected, onSelect }) {
  const noGps = !hasLocation(v);
  const stale = isStale(v);
  const ref = useRef(null);
  useEffect(() => {
    if (selected && ref.current?.scrollIntoView) ref.current.scrollIntoView({ block: "nearest" });
  }, [selected]);
  return (
    <li>
      <button
        ref={ref}
        type="button"
        className={`tc-item ${noGps ? "tc-no-gps" : "tc-has-gps"}${selected ? " is-selected" : ""}`}
        aria-pressed={selected}
        onClick={() => onSelect(v.rideId)}
      >
        <div className="tc-item-header">
          <span className="tc-item-activity">
            <i className={`fas fa-${v.activityTypeId === 2 ? "map-marker-alt" : "plane fa-rotate-270"}`} aria-hidden="true" />
            {v.activityDisplay}
          </span>
          <span className="tc-status-badge tc-badge-orange">{v.tripStatusDescription}</span>
        </div>
        <div className="tc-item-vehicle">
          <span className="tc-item-vehicle-chip">
            <i className="fas fa-car" aria-hidden="true" />
            {v.vehicleNo}
          </span>
          <span className={`tc-gps-dot ${noGps ? "tc-gps-dot-off" : stale ? "tc-gps-dot-stale" : "tc-gps-dot-on"}`} aria-hidden="true" />
          {noGps && <span className="tc-item-nogps-label">No GPS</span>}
          {stale && (
            <span className="tc-item-nogps-label trk-stale-label" title={`Last GPS ${formatTime(v.gpsDateTime)}`}>
              Stale GPS
            </span>
          )}
        </div>
        <div className="tc-item-grid">
          {v.driverName && (
            <div className="tc-item-field">
              <i className="fas fa-id-badge" aria-hidden="true" />
              <span className="tc-item-field-value">{v.driverName}</span>
            </div>
          )}
          {v.gpsDateTime && (
            <div className="tc-item-field">
              <i className="far fa-clock" aria-hidden="true" />
              <span className="tc-item-field-value">{formatTime(v.gpsDateTime)}</span>
            </div>
          )}
          {v.paxName && (
            <div className="tc-item-field">
              <i className="fas fa-user" aria-hidden="true" />
              <span className="tc-item-field-value">{v.paxName}</span>
            </div>
          )}
        </div>
        <div className="tc-item-footer">
          <span className="tc-item-rideid">#{v.rideId}</span>
        </div>
      </button>
    </li>
  );
}

/**
 * "Vehicles on job" beside the map: a search box and the vehicle cards (20 at a time). A single click selects a vehicle;
 * the selected card is highlighted and scrolled into view (also when picked on the map).
 */
export default function VehicleList({ rows, total, noGps, selectedId, loading, onSelect }) {
  const [search, setSearch] = useState("");
  const [shown, setShown] = useState(PAGE);
  const term = search.trim().toLowerCase();
  const found = useMemo(() => (term ? rows.filter((v) => searchText(v).includes(term)) : rows), [rows, term]);
  const selectedAt = found.findIndex((v) => v.rideId === selectedId);
  // A vehicle picked on the map must be visible in the list, even beyond the shown page.
  const limit = Math.max(shown, selectedAt + 1);

  useEffect(() => setShown(PAGE), [term]);

  return (
    <div className="tc-list-panel">
      <div className="tc-list-header">
        <i className="fas fa-truck-moving" aria-hidden="true" />
        <span>Vehicles on job</span>
        <span className="tc-list-header-count" title="Vehicles on job">
          {total}
        </span>
        {noGps > 0 && (
          <span className="tc-list-header-count tc-list-header-nogps" title="Vehicles without a GPS position">
            {noGps} no GPS
          </span>
        )}
      </div>
      <div className="trk-search">
        <i className="fas fa-search" aria-hidden="true" />
        <input
          type="search"
          placeholder="Search vehicle, driver, crew…"
          aria-label="Search vehicles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <ul className="trk-list" aria-label="Vehicles on job" aria-busy={loading}>
        {found.slice(0, limit).map((v) => (
          <VehicleCard key={v.rideId} v={v} selected={v.rideId === selectedId} onSelect={onSelect} />
        ))}
        {!found.length && <li className="trk-empty">{loading ? "Loading…" : term ? "No vehicle matches the search." : "No vehicle on job."}</li>}
        {found.length > limit && (
          <li>
            <button type="button" className="btn btn-link btn-sm trk-more" onClick={() => setShown(limit + PAGE)}>
              Show more ({found.length - limit})
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}
